import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import Button from "@mui/material/Button";
import Tooltip from '@mui/material/Tooltip';
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CustomLoader from "../components/CustomLoader";
import { getSpecialStudents, updateStudentPermissions } from "../services/adminStudents";
import { CustomAlert, showToastAlert } from "./components/CustomAlert";
import DataTable from "./components/DataTable";
import StudentInfo from "./modals/StudentInfo";

const PERMISSION_OPTIONS = ["WRITE_BLOG", "WRITE_POST", "WRITE_NAME", "WRITE_EMAIL"];

const AdminAllSpecialUserList = () => {
    const [loaderOpen, setLoaderOpen] = React.useState(true);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [apiData, setApiData] = React.useState([]);
    const [selectedOptions, setSelectedOptions] = useState(new Map());
    const [currSpecialUserId, setCurrSpecialUserId] = React.useState("");
    const [permissionModalOpen, setPermissionModalOpen] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);
    const [editingPermissions, setEditingPermissions] = useState([]);
    const admin = useSelector((state) => state.admin);


    const tableColumns = [
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            editable: false,
            renderCell: (params) => (
                <p>{params.row.name}</p>
            ),
        },
        {
            field: "email",
            headerName: "Email",
            flex: 1,
            editable: false,
            renderCell: (params) => (
                <p>{params.row.email}</p>
            ),
        },
        {
            field: "permissions",
            headerName: "Permissions",
            flex: 2,
            editable: false,
            renderCell: (params) => {
                const permissions = Array.isArray(params.row?.permissions) ? params.row.permissions : [];
                return (
                    <div style={{ display: "flex", flexWrap: "wrap" }}>
                        {permissions.length > 0 ? (
                            permissions.map((perm) => (
                                <Chip
                                    key={perm}
                                    label={perm}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            ))
                        ) : (
                            <p style={{ margin: 0 }}>No permissions</p>
                        )}
                    </div>
                );
            },
        },
        {
            field: "createdAt",
            headerName: "Created At",
            flex: 1,
            editable: false,
            renderCell: (params) => (
                <p >
                    {new Date(params.row?.createdAt).toLocaleString()}
                </p>
            ),
        },
        {
            field: "action",
            headerName: "Action",
            flex: 1,
            minWidth: 230,
            editable: false,
            renderCell: (params) => (
                <div>
                    <Tooltip title="Show SpecialUser Details">
                        <Button
                            variant="outlined"
                            onClick={() => handleStudentView(params)}
                            className="circular-btn me-2"
                            startIcon={<RemoveRedEyeRoundedIcon />}
                        />
                    </Tooltip>
                    <Tooltip title="Update Permissions">
                        <Button
                            variant="outlined"
                            onClick={() => handleSpecialUserPermissionUpdate(params)}
                            className="circular-btn me-2"
                        >
                            Update
                        </Button>
                    </Tooltip>
                </div>
            ),
        },
    ];

    useEffect(() => {
        getSpecialUsers()
    }, [admin]);

    const getSpecialUsers = async () => {
        try {
            setLoaderOpen(true);
            const response = await getSpecialStudents(admin.token);
            if(response.status !== 200) {
                showToastAlert(response.data.msg, "error");
                return;
            }
            setApiData(response.data.students);
            //set initial permissions map
            let tempPermissionsMap = new Map();
            response.data.students.map((item) => {
                const perms = Array.isArray(item.permissions) ? item.permissions : [];
                tempPermissionsMap.set(item._id, perms);
            })
            setSelectedOptions(tempPermissionsMap);
            setLoaderOpen(false);
        }
        catch (err) {
            throw new Error(err)
        }
    }

    //update special user permissions function
    const handleSpecialUserPermissionUpdate = (params) => {
        const id = params.row._id;
        const currentPermissions = Array.isArray(params.row?.permissions) ? params.row.permissions : [];
        
        setEditingUserId(id);
        setEditingPermissions(currentPermissions);
        setPermissionModalOpen(true);
    }

    const handlePermissionChange = (permission) => {
        setEditingPermissions((prev) => {
            if (prev.includes(permission)) {
                return prev.filter((p) => p !== permission);
            } else {
                return [...prev, permission];
            }
        });
    }

    const handlePermissionSave = async () => {
        try {
            const response = await updateStudentPermissions(editingUserId, editingPermissions, admin.token);
            
            if(response.status !== 200) {
                showToastAlert(response.data.msg, "error");
                return;
            }
            
            //update the row in the table
            setApiData((prevApiData) => 
                prevApiData.map((row) => 
                    row._id === editingUserId ? { ...row, permissions: editingPermissions } : row
                )
            );
            
            showToastAlert("Permissions updated successfully", "success");
            setPermissionModalOpen(false);
            setEditingUserId(null);
            setEditingPermissions([]);
        } catch (err) {
            showToastAlert("Error updating permissions", "error");
        }
    }

    const handlePermissionClose = () => {
        setPermissionModalOpen(false);
        setEditingUserId(null);
        setEditingPermissions([]);
    }

    const handleStudentView = (params) => {
        const id = params.row._id;  
        setCurrSpecialUserId(id);
        setModalOpen(true);
    };

    const closeStudentView = () => {
        setModalOpen(false);
    }



    return (
        <div>
            <h1 className="page-title-1 my-3 asdsd">Employer SpecialUsers</h1>
            <CustomLoader setLoaderOpen={setLoaderOpen} loaderOpen={loaderOpen} />
            <DataTable data={apiData} columns={tableColumns} id="_id" />
            <CustomAlert />

            {/* SpecialUser Info Modal */}
            {modalOpen && (
                <StudentInfo
                    visible={modalOpen}
                    onClose={closeStudentView}
                    studentId={currSpecialUserId}
                />
            )}

            {/* Permission Selection Dialog */}
            <Dialog open={permissionModalOpen} onClose={handlePermissionClose} maxWidth="sm" fullWidth>
                <DialogTitle>Update User Permissions</DialogTitle>
                <DialogContent>
                    <FormGroup sx={{ marginTop: 2 }}>
                        {PERMISSION_OPTIONS.map((permission) => (
                            <FormControlLabel
                                key={permission}
                                control={
                                    <Checkbox
                                        checked={editingPermissions.includes(permission)}
                                        onChange={() => handlePermissionChange(permission)}
                                    />
                                }
                                label={permission}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePermissionClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handlePermissionSave} variant="contained" color="primary">
                        Save Permissions
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AdminAllSpecialUserList;
